// frontend/src/pages/admin/TicketSettingsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Spin } from 'antd';
import TicketStatuses from './TicketStatuses';
import TicketTypes from './TicketTypes';
import TicketCategories from './TicketCategories';
import TicketSettings from './TicketSettings';
import TicketPriorities from './TicketPriorities';
import AssetTypes from './AssetTypes';
import { useAuth } from '../../context/AuthContext';
import './AdminPanelTabs.css';

const { TabPane } = Tabs;

const TicketSettingsPage = () => {
  const { 
    user, 
    loading, 
    hasPermission,
    PERMISSION_LEVELS,
    initialized 
  } = useAuth();
  const [activeTabKey, setActiveTabKey] = useState('general-settings');

  // Define tabs with their permission requirements
  const tabs = useMemo(() => [
    {
      key: 'general-settings',
      tab: 'General Settings',
      content: <TicketSettings />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'ticket-statuses',
      tab: 'Ticket Statuses',
      content: <TicketStatuses />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'ticket-types',
      tab: 'Ticket Types',
      content: <TicketTypes />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'ticket-categories',
      tab: 'Ticket Categories',
      content: <TicketCategories />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'ticket-priorities',
      tab: 'Ticket Priorities',
      content: <TicketPriorities />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'ticket-assets',
      tab: 'Asset Types',
      content: <AssetTypes />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    }
  ], [PERMISSION_LEVELS]);

  // Filter tabs based on permissions
  const accessibleTabs = useMemo(() => {
    if (!initialized || loading || !user) {
      return [];
    }
    return tabs.filter(tab => 
      hasPermission(
        tab.requiredPermission.resource,
        tab.requiredPermission.action,
        tab.requiredPermission.level
      )
    );
  }, [tabs, hasPermission, initialized, loading, user]);

  // Ensure a valid active tab is selected
  useEffect(() => {
    if (initialized && !loading && user && accessibleTabs.length > 0) {
      if (!accessibleTabs.some(tab => tab.key === activeTabKey)) {
        setActiveTabKey(accessibleTabs[0].key);
      }
    }
  }, [activeTabKey, initialized, loading, user, accessibleTabs]);

  // Loading state
  if (!initialized || loading || !user) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Spin size="large" tip="Loading Ticket Settings..." />
      </div>
    );
  }

  // No access state
  if (accessibleTabs.length === 0) {
    return (
      <div className="admin-page-container">
        <h1 className="page-title">Ticket Settings</h1>
        <p className="page-description">You do not have permission to view any ticket settings tabs.</p>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <h1 className="page-title">Ticket Settings</h1>
      <p className="page-description">
        Manage configurations for ticket generation, statuses, types, categories, priorities and assets.
      </p>

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        className="admin-panel-tabs"
      >
        {accessibleTabs.map(tab => (
          <TabPane tab={tab.tab} key={tab.key}>
            {tab.content}
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default TicketSettingsPage;